import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../components/ToastConfig';

// 定義驗證規則
const loginSchema = z.object({
  userName: z.string()
    .min(1, '請輸入信箱')
    .email('信箱格式不正確'),
  pwd: z.string()
    .min(1, '請輸入密碼')
    .min(6, '密碼至少需要6個字元')
});

function Login() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { 
    register, 
    handleSubmit, 
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data) => {
    try {
      const result = await login(data?.userName, data?.pwd);
      if (result?.success) {
        if (result?.data?.requirePasswordChange) {
          showToast.warning('請修改密碼');
          navigate("/profile", { replace: true });
        } else {
          const from = location.state?.from?.pathname || '/profile';
          navigate(from, { replace: true });  
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            登入
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="mb-4">
              <label htmlFor="userName" className="block text-sm font-medium text-gray-700">
                信箱
              </label>
              <input
                id="userName"
                type="email"
                {...register('userName')}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                  errors.userName ? 'border-red-500' : ''
                }`}
              />
              {errors.userName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.userName.message}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="pwd" className="block text-sm font-medium text-gray-700">
                密碼
              </label>
              <div className='flex relative items-center mt-1'>
                <input
                  id="pwd"
                  type={isPasswordVisible ? 'text' : 'password'}
                  {...register('pwd')}
                  className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    errors.pwd ? 'border-red-500' : ''
                  }`}
                />
                {isPasswordVisible ? (
                  <Eye className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-fourth mr-1.5" size={20} onClick={() => setIsPasswordVisible(false)} />
                ) : (
                  <EyeOff className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-fourth mr-1.5" size={20}  onClick={() => setIsPasswordVisible(true)} />
                )}
              </div>
              {errors.pwd && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.pwd.message}
                </p>
              )}
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
          >
            {isSubmitting ? '登入中...' : '登入'}
          </button>
        </form>
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            忘記密碼
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;